import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Apple,
  Globe,
  Loader2,
  Monitor,
  Smartphone,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { apiFetch, apiJson } from '@/lib/api/client'
import { formatDate, parseBrowser } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Eyebrow } from '@/components/hawk-primitives'

interface SessionEntry {
  sessionId: string
  createdAt: string
  lastActiveAt: string
  ipAddress: string | null
  userAgent: string | null
  current: boolean
}

export function ActiveSessions() {
  const queryClient = useQueryClient()
  const [revokingAll, setRevokingAll] = useState(false)

  const sessions = useQuery({
    queryKey: ['active-sessions'],
    queryFn: () => apiJson<SessionEntry[]>('/auth/sessions'),
  })

  const otherSessionCount = (sessions.data ?? []).filter((s) => !s.current).length

  async function handleRevoke(sessionId: string) {
    const response = await apiFetch(
      `/auth/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      throw new Error((await response.text()) || 'Failed to revoke session')
    }
    queryClient.invalidateQueries({ queryKey: ['active-sessions'] })
  }

  async function handleRevokeAll() {
    setRevokingAll(true)
    try {
      const response = await apiFetch('/auth/sessions', { method: 'DELETE' })
      if (!response.ok) {
        throw new Error((await response.text()) || 'Failed to revoke sessions')
      }
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] })
    } finally {
      setRevokingAll(false)
    }
  }

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex items-baseline gap-3">
          <Eyebrow>Active sessions</Eyebrow>
          {sessions.data && (
            <span className="font-mono text-xs text-muted-foreground tabular-nums">
              {sessions.data.length} signed in
            </span>
          )}
        </div>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        {sessions.isLoading ? (
          <div className="flex items-center gap-2 px-5 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading sessions...
          </div>
        ) : sessions.data && sessions.data.length > 0 ? (
          <>
            <ul className="divide-y divide-border">
              {sessions.data.map((s) => (
                <SessionRow key={s.sessionId} session={s} onRevoke={handleRevoke} />
              ))}
            </ul>
            {otherSessionCount > 0 && (
              <div className="px-5 py-3 border-t border-border bg-background/30 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground">
                  Revoke any session you don't recognize.
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRevokeAll}
                  disabled={revokingAll}
                >
                  {revokingAll
                    ? 'Revoking...'
                    : `Revoke all other sessions (${otherSessionCount})`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <p className="px-5 py-10 text-sm text-muted-foreground text-center">
            No active sessions found.
          </p>
        )}
      </div>
    </section>
  )
}

function deviceIcon(ua: string | null): LucideIcon {
  if (!ua) return Globe
  if (/iPhone|Android|Mobile/i.test(ua)) return Smartphone
  if (/Mac OS|iPad/i.test(ua)) return Apple
  return Monitor
}

function SessionRow({
  session,
  onRevoke,
}: {
  session: SessionEntry
  onRevoke: (id: string) => Promise<void>
}) {
  const [revoking, setRevoking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const browser = parseBrowser(session.userAgent)
  const Icon = deviceIcon(session.userAgent)

  return (
    <li className="group flex items-center gap-4 px-5 py-4 relative hover:bg-popover transition-colors">
      {session.current && (
        <span
          className="absolute left-0 top-3 bottom-3 w-[2px] bg-primary"
          aria-hidden
        />
      )}
      <div className="flex items-center justify-center size-10 rounded-md bg-secondary border border-border text-muted-foreground shrink-0">
        <Icon className="size-4" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{browser}</span>
          {session.current && (
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-primary/10 text-primary ring-1 ring-inset ring-primary/30">
              This device
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-muted-foreground">
          {session.ipAddress && (
            <span className="font-mono uppercase tracking-wider">
              {session.ipAddress}
            </span>
          )}
          <span>Active {formatDate(session.lastActiveAt)}</span>
        </div>
        {error && <div className="text-xs text-destructive mt-1">{error}</div>}
      </div>
      {!session.current && (
        <Button
          size="sm"
          variant="ghost"
          disabled={revoking}
          className="text-muted-foreground hover:text-destructive"
          onClick={async () => {
            setRevoking(true)
            setError(null)
            try {
              await onRevoke(session.sessionId)
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Failed')
            } finally {
              setRevoking(false)
            }
          }}
        >
          {revoking ? 'Revoking...' : 'Revoke'}
        </Button>
      )}
    </li>
  )
}
